/*!
 * ThermoAspen Sauna Designer — Layout Shell v1.6.1
 * Stage 2 of the mobile rebuild (25 Oct 2026); navigation update 26 Oct 2026.
 *
 * 1.6.1 (29 Oct 2026): the door is edited in the window editors, so the Layout "Door" row is
 * gone and "Windows" is now "Windows/Door" (its value also names the door's wall). The
 * Interior row that opens the 3D window editor is renamed to match.
 *
 * 1.5.0: one step bar for Layout · Interior · Exterior · Summary (Summary is a step of its
 * own, shown inline); the step chip is gone; "Floor plan" is now "Layout". Every step is a
 * clean viewer, a short list of rows, then one Next button. Interior rows: Wall finish and
 * Window view. The viewers lose their extra buttons (zoom, jumps, Adjust Windows) and get
 * one expand button; zoom is by pinch or mouse wheel.
 *
 * 1.6.0 (Stage 8, 28 Oct 2026): wide screens (1024 px+) show each viewer on the left and its
 * rows on the right (CSS). Step changes are announced to screen readers, and Next moves the
 * keyboard focus to the new step's heading. Floor-plan labels stay readable on small screens:
 * when the plan is drawn small, the wall and bench names are enlarged and the stove's detail
 * labels are hidden (they come back with "Show stove clearance"). --ta-shell-h = height of the
 * step bar, for the wide-screen layout.
 *
 * Adds the mobile-first frame around the existing Designer components:
 *   - a sticky bar with the step chip, project menu and Layout / Interior / Exterior tabs
 *   - one visible tab panel at a time (floor plan, interior 3D, exterior 3D)
 *   - a fixed bottom bar with the auto-save tick and a Next button
 *   - a bottom-sheet host (used for the workflow steps, project actions and ⓘ texts)
 *   - Exterior: Style / Cladding / Roof rows under the 3D view, each opening a short settings panel
 *   - Layout: Room / Windows/Door / Stove / Benches rows under the floor plan; clearance toggle
 *   - ⓘ buttons that read their text from assets/thermoaspen-designer-info-text.js
 *
 * It never edits the components' code or data. It only shows/hides their existing
 * sections, moves the workflow nav and project actions into sheets while open, and
 * tells the 3D viewers to resize when their tab appears. If this file fails to load,
 * the page keeps its previous long-scroll layout.
 *
 * Public API: window.ThermoAspenDesignerShell
 *   .version, .showTab(name), .currentTab(),
 *   .openSheet({title, node|html, infoKey}), .closeSheet(), .openInfo(key)
 */
(function (W, D) {
  'use strict';
  if (W.ThermoAspenDesignerShell) return;

  var VERSION = '1.6.1';
  var ROOT_ID = 'ta-shell';
  var TABS = [
    { name: 'layout', label: 'Layout', heading: 'Layout', info: 'layout.floorPlan', root: 'ti-sauna-layout', next: 'interior', nextLabel: 'Next: Interior' },
    { name: 'interior', label: 'Interior', heading: 'Interior', info: 'interior.view', root: 'ti-sauna-3d-preview', next: 'exterior', nextLabel: 'Next: Exterior' },
    { name: 'exterior', label: 'Exterior', heading: 'Exterior', info: 'exterior.style', root: 'ti-sauna-exterior-viewer', next: 'summary', nextLabel: 'Next: Summary' },
    { name: 'summary', label: 'Summary', heading: 'Your sauna', info: 'summary.design', root: 'ta-summary-step', next: 'construction', nextLabel: 'Construction drawings' }
  ];
  var STORE_KEY = 'ThermoAspenDesignerShellTabV1';
  var USER_GESTURE_WINDOW_MS = 1500;

  var shell, bar, bottomEl, sheet, sheetBody, sheetTitle, sheetInfoBtn, backdrop, saveTick, saveLabel, nextBtn;
  var panels = {};        // tab name -> { wrap, root, tab, def }
  var active = null;
  var lastGesture = 0;
  var sheetState = null;  // { node, placeholder, onClose, returnFocus }
  var infoData = null, infoPromise = null;
  var nativeScrollIntoView = Element.prototype.scrollIntoView;

  function $(id) { return D.getElementById(id); }
  function el(tag, cls, text) { var e = D.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
  function sectionWrap(node) {
    var n = node;
    while (n && n.parentElement && n.parentElement !== D.body) {
      if (n.classList && n.classList.contains('shopify-section')) return n;
      n = n.parentElement;
    }
    return node;
  }
  function tabDef(name) { for (var i = 0; i < TABS.length; i++) if (TABS[i].name === name) return TABS[i]; return null; }
  function store(v) { try { if (v === undefined) return W.sessionStorage.getItem(STORE_KEY); W.sessionStorage.setItem(STORE_KEY, v); } catch (e) { return null; } }

  var INFO_SVG = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg>';
  function infoButton(key, label) {
    var b = el('button', 'ta-info-btn');
    b.type = 'button';
    b.setAttribute('data-ta-info', key);
    b.setAttribute('aria-label', label || 'More information');
    b.innerHTML = INFO_SVG;
    return b;
  }

  /* ---------- info texts ---------- */
  /* The texts live in assets/thermoaspen-designer-info-text.js, which sets
     window.ThermoAspenDesignerInfoText. It is loaded the first time an ⓘ is tapped. */
  function loadInfo() {
    if (infoData) return Promise.resolve(infoData);
    if (W.ThermoAspenDesignerInfoText) { infoData = W.ThermoAspenDesignerInfoText; return Promise.resolve(infoData); }
    if (infoPromise) return infoPromise;
    var src = shell && shell.getAttribute('data-info-src');
    if (!src) return Promise.resolve(null);
    infoPromise = new Promise(function (resolve) {
      var s = D.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { infoData = W.ThermoAspenDesignerInfoText || null; if (!infoData) infoPromise = null; resolve(infoData); };
      s.onerror = function () { infoPromise = null; try { console.warn('ThermoAspen Designer shell: info texts unavailable'); } catch (e) {} resolve(null); };
      D.head.appendChild(s);
    });
    return infoPromise;
  }
  function openInfo(key) {
    var holder = el('div', 'ta-info-body');
    holder.appendChild(el('p', 'ta-info-loading', 'Loading…'));
    openSheet({ title: 'Information', node: holder, kind: 'info' });
    loadInfo().then(function (data) {
      if (!sheetState || sheetState.node !== holder) return;
      var entry = data && data[key];
      holder.innerHTML = '';
      if (!entry) { holder.appendChild(el('p', null, 'This information isn’t available right now.')); return; }
      sheetTitle.textContent = entry.title || 'Information';
      (entry.body || []).forEach(function (line) { holder.appendChild(el('p', null, String(line))); });
    });
  }

  /* ---------- bottom sheet ---------- */
  function openSheet(opts) {
    opts = opts || {};
    closeSheet(true);
    var node = opts.node;
    if (!node && opts.html != null) { node = el('div'); node.innerHTML = opts.html; }
    if (!node) return;
    var placeholder = null;
    if (node.parentNode) { placeholder = D.createComment('ta-shell sheet placeholder'); node.parentNode.insertBefore(placeholder, node); }
    sheetState = { node: node, placeholder: placeholder, onClose: opts.onClose, returnFocus: D.activeElement, kind: opts.kind || '' };
    sheetTitle.textContent = opts.title || '';
    sheetInfoBtn.hidden = !opts.infoKey;
    if (opts.infoKey) sheetInfoBtn.setAttribute('data-ta-info', opts.infoKey);
    sheet.setAttribute('data-kind', sheetState.kind);
    sheetBody.innerHTML = '';
    sheetBody.appendChild(node);
    sheet.hidden = false; backdrop.hidden = false;
    D.documentElement.classList.add('ta-shell-sheet-open');
    W.requestAnimationFrame(function () { sheet.classList.add('is-open'); backdrop.classList.add('is-open'); });
    var focusTarget = sheet.querySelector('.ta-sheet-close');
    if (focusTarget) try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
  }
  function closeSheet(immediate) {
    if (!sheetState) return;
    var s = sheetState; sheetState = null;
    if (s.placeholder && s.placeholder.parentNode) s.placeholder.parentNode.replaceChild(s.node, s.placeholder);
    else if (s.node.parentNode === sheetBody) sheetBody.removeChild(s.node);
    sheet.classList.remove('is-open'); backdrop.classList.remove('is-open');
    sheet.hidden = true; backdrop.hidden = true;
    D.documentElement.classList.remove('ta-shell-sheet-open');
    if (typeof s.onClose === 'function') try { s.onClose(); } catch (e) {}
    if (!immediate && s.returnFocus && typeof s.returnFocus.focus === 'function') try { s.returnFocus.focus({ preventScroll: true }); } catch (e) {}
  }
  function trapFocus(e) {
    if (!sheetState || e.key !== 'Tab') return;
    var f = sheet.querySelectorAll('button:not([disabled]):not([hidden]),[href],input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])');
    var list = [];
    for (var i = 0; i < f.length; i++) if (f[i].offsetParent !== null) list.push(f[i]);
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && D.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- tabs ---------- */
  function notifyResize() {
    W.requestAnimationFrame(function () {
      try { W.dispatchEvent(new Event('resize')); } catch (e) {}
      W.setTimeout(function () { try { W.dispatchEvent(new Event('resize')); } catch (e) {} }, 120);
    });
  }
  function showTab(name, opts) {
    opts = opts || {};
    var def = tabDef(name);
    if (!def || !panels[name]) return false;
    if (active === name && !opts.force) return true;
    var prev = active;
    active = name;
    D.documentElement.setAttribute('data-ta-tab', name);
    TABS.forEach(function (t) {
      var p = panels[t.name]; if (!p) return;
      var on = t.name === name;
      p.wrap.classList.toggle('is-active', on);
      p.tab.setAttribute('aria-selected', on ? 'true' : 'false');
      p.tab.tabIndex = on ? 0 : -1;
    });
    nextBtn.textContent = def.nextLabel;
    nextBtn.setAttribute('data-next', def.next || 'construction');
    store(name);
    if (opts.updateHash !== false) {
      try { W.history.replaceState(W.history.state, '', W.location.pathname + W.location.search + '#' + name); } catch (e) {}
    }
    if (prev !== null && !opts.keepScroll) {
      var top = shell.getBoundingClientRect().top + W.pageYOffset - stickyOffset();
      if (W.pageYOffset > top) W.scrollTo(0, Math.max(0, top));
    }
    notifyResize();
    if (prev !== null) announce(def.heading + ', step ' + (TABS.indexOf(def) + 1) + ' of ' + TABS.length);
    if (opts.focusHeading) focusHeading(name);
    setTimeout(tunePlanLabels, 60);
    try { W.dispatchEvent(new CustomEvent('ThermoAspenDesignerTabChanged', { detail: { tab: name, previous: prev } })); } catch (e) {}
    return true;
  }
  /* 1.6.0 — screen-reader announcement of the step, and focus to the step heading after Next. */
  var liveEl = null;
  function announce(text) {
    if (!liveEl) {
      liveEl = el('div', 'ta-sr-only'); liveEl.setAttribute('role', 'status'); liveEl.setAttribute('aria-live', 'polite');
      D.body.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = text; }, 30);
  }
  function focusHeading(name) {
    var p = panels[name], h = p && p.wrap.querySelector('.ta-panel-title');
    if (!h) return;
    if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
    try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
  }

  /* 1.6.0 — floor-plan labels. The plan is an SVG drawn in its own units and scaled to fit, so
     on a phone its 8-unit labels end up about 3 px tall. When the plan is drawn below 75 % size,
     the workspace gets .ta-plan-small and --ta-plan-u (screen px -> plan units); the CSS then sets
     the wall and bench names to a readable screen size and hides the stove detail labels. */
  var planObs = null, planRaf = 0, planSvg = null;
  function planSchedule() {
    if (planRaf) return;
    var run = function () { planRaf = 0; tunePlanLabels(); };
    planRaf = W.requestAnimationFrame ? W.requestAnimationFrame(run) : setTimeout(run, 16);
  }
  function tunePlanLabels() {
    var ws = $('ti-stove-workspace'); if (!ws) return;
    var svg = ws.querySelector('svg'); if (!svg || !svg.getScreenCTM) return;
    if (!planObs && 'MutationObserver' in W) {
      planObs = new MutationObserver(planSchedule);
      planObs.observe(ws, { childList: true, subtree: true });
    }
    if (planObs && svg !== planSvg) { planSvg = svg; planObs.observe(svg, { attributes: true, attributeFilter: ['viewBox', 'width', 'height'] }); }
    var m = null; try { m = svg.getScreenCTM(); } catch (e) { m = null; }
    var k = m ? Math.abs(m.a) : 0;
    if (!(k > 0) || !svg.getBoundingClientRect().width) return;
    var u = (1 / k).toFixed(3), small = k < 0.75;
    if (ws.classList.contains('ta-plan-small') !== small) ws.classList.toggle('ta-plan-small', small);
    if (ws.style.getPropertyValue('--ta-plan-u') !== u) ws.style.setProperty('--ta-plan-u', u);
  }

  function stickyOffset() {
    var h = parseFloat(getComputedStyle(D.documentElement).getPropertyValue('--ta-shell-top')) || 0;
    return h;
  }
  function tabForNode(node) {
    for (var i = 0; i < TABS.length; i++) {
      var p = panels[TABS[i].name];
      if (p && p.wrap.contains(node)) return TABS[i].name;
    }
    return null;
  }

  /* When a component scrolls to something in another tab (e.g. the floor plan's
     "Interior" button, or "use the wall editor below"), open that tab first.
     Only within 1.5 s of a user action, so automatic scrolls never switch tabs. */
  function patchScrollIntoView() {
    Element.prototype.scrollIntoView = function () {
      try {
        var t = tabForNode(this);
        if (t && t !== active && Date.now() - lastGesture < USER_GESTURE_WINDOW_MS) {
          showTab(t, { keepScroll: true });
          var p = panels[t];
          if (p && (this === p.root || this === p.wrap)) {
            var top = p.wrap.getBoundingClientRect().top + W.pageYOffset - stickyOffset() - 8;
            var shellTop = shell.getBoundingClientRect().top + W.pageYOffset - stickyOffset();
            W.scrollTo(0, Math.max(0, Math.min(top, shellTop)));
            return;
          }
        } else if (t && t !== active) {
          return; /* target is in a hidden tab and no user action: ignore */
        }
      } catch (e) {}
      return nativeScrollIntoView.apply(this, arguments);
    };
  }

  /* ---------- save status ---------- */
  var saveTimer = 0;
  function markSaving() {
    bottomEl.classList.add('is-saving');
    saveLabel.textContent = 'Saving…';
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      bottomEl.classList.remove('is-saving');
      saveLabel.textContent = 'Saved on this device';
    }, 700);
  }

  /* ---------- sticky offset (theme header) ---------- */
  function measureHeader() {
    var top = 0;
    var cands = D.querySelectorAll('#header-group header, header-component, .header-wrapper, #shopify-section-header, [id*="__header"]');
    for (var i = 0; i < cands.length; i++) {
      var cs = getComputedStyle(cands[i]);
      if ((cs.position === 'sticky' || cs.position === 'fixed') && cands[i].offsetHeight > 0) {
        var r = cands[i].getBoundingClientRect();
        if (r.top <= 1) top = Math.max(top, Math.round(r.bottom));
        else if (cs.position === 'sticky') top = Math.max(top, cands[i].offsetHeight);
      }
    }
    D.documentElement.style.setProperty('--ta-shell-top', top + 'px');
  }


  /* ---------- exterior tools (Style / Cladding / Roof rows under the 3D view) ----------
     The exterior controls stay inside their own section (the exterior scripts look them up
     there). While a row is open, CSS shows #tiext-controls as a panel directly under the 3D
     view with only that group's settings. */
  var EXT_GROUPS = [
    { key: 'style', label: 'Building Type', info: 'exterior.style', match: function (c) { return !!c.querySelector('#tiext-style'); }, value: function () { return selText('#tiext-style'); } },
    { key: 'roof', label: 'Roof Details', info: 'exterior.roof', match: function (c) { return c.id === 'tiext-design-settings'; }, value: function () {
        var host = $('tiext-design-settings'); if (!host) return '';
        var sels = host.querySelectorAll('select'); var sel = sels[sels.length - 1];
        var t = sel && sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
        var m = /RAL\s*\d+/.exec(t); return m ? m[0] : t; } },
    { key: 'cladding', label: 'Exterior Cladding', info: 'exterior.cladding', match: function (c) { return !!c.querySelector('#tiext-scheme'); }, value: function () { return selText('#tiext-scheme'); } }
  ];
  var ext = { controls: null, rows: {}, head: null, title: null, info: null, open: null };
  function selText(q) { var s = D.querySelector(q); return s && s.options && s.options[s.selectedIndex] ? s.options[s.selectedIndex].text : ''; }
  function extRefresh() {
    if (!ext.controls) return;
    var ready = !ext.controls.hidden || !!ext.open;
    EXT_GROUPS.forEach(function (g) {
      var r = ext.rows[g.key]; if (!r) return;
      r.btn.disabled = !ready;
      r.val.textContent = ready ? (g.value() || '') : '…';
    });
  }
  function extTag() {
    Array.prototype.forEach.call(ext.controls.children, function (c) {
      if (c === ext.head) return;
      var g = null;
      EXT_GROUPS.forEach(function (x) { if (!g && x.match(c)) g = x.key; });
      if (g) c.setAttribute('data-ta-group', g); else c.removeAttribute('data-ta-group');
    });
  }
  function extOpen(key) {
    var g = null; EXT_GROUPS.forEach(function (x) { if (x.key === key) g = x; });
    if (!g || !ext.controls) return;
    closeSheet(true);
    extTag();
    ext.open = key;
    Object.keys(ext.rows).forEach(function (k) { ext.rows[k].btn.setAttribute('aria-expanded', k === key ? 'true' : 'false'); });
    ext.title.textContent = g.label;
    ext.info.setAttribute('data-ta-info', g.info);
    D.documentElement.setAttribute('data-ta-tool', key);
    ext.wasHidden = ext.controls.hidden;
    ext.controls.hidden = false;
    // Bring the panel into view with a strip of the building still showing above it.
    var top = ext.controls.getBoundingClientRect().top + W.pageYOffset - stickyOffset() - (shell.offsetHeight || 0) - 140;
    W.scrollTo(0, Math.max(0, top));
    try { ext.done.focus({ preventScroll: true }); } catch (e) {}
  }
  function extClose() {
    if (!ext.open) return;
    var key = ext.open; ext.open = null;
    Object.keys(ext.rows).forEach(function (k) { ext.rows[k].btn.setAttribute('aria-expanded', 'false'); });
    D.documentElement.removeAttribute('data-ta-tool');
    if (ext.wasHidden) ext.controls.hidden = true;
    extRefresh();
    var r = ext.rows[key]; if (r) try { r.btn.focus({ preventScroll: true }); } catch (e) {}
  }
  function setupExteriorTools() {
    var controls = $('tiext-controls'), stage = $('tiext-stage');
    if (!controls || !stage || !stage.parentNode) return;
    ext.controls = controls;
    controls.classList.add('ta-tool-panel');
    controls.setAttribute('role', 'region');
    controls.setAttribute('aria-label', 'Exterior settings');

    var head = el('div', 'ta-tool-head');
    ext.title = el('h3', 'ta-tool-title', '');
    ext.info = infoButton('exterior.style', 'More information');
    ext.done = el('button', 'ta-tool-done', 'Done'); ext.done.type = 'button';
    head.appendChild(ext.title); head.appendChild(ext.info); head.appendChild(ext.done);
    controls.insertBefore(head, controls.firstChild);
    ext.head = head;
    on(ext.done, 'click', extClose);

    var list = el('div', 'ta-tool-rows');
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', 'Exterior settings');
    EXT_GROUPS.forEach(function (g) {
      var b = el('button', 'ta-tool-row'); b.type = 'button';
      b.setAttribute('data-ta-tool-open', g.key);
      var name = el('span', 'ta-tool-row-name', g.label);
      var val = el('span', 'ta-tool-row-value', '…');
      var chev = el('span', 'ta-tool-row-chev'); chev.setAttribute('aria-hidden', 'true');
      chev.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"></path></svg>';
      b.appendChild(name); b.appendChild(val); b.appendChild(chev);
      on(b, 'click', function () { if (ext.open === g.key) extClose(); else extOpen(g.key); });
      list.appendChild(b);
      ext.rows[g.key] = { btn: b, val: val };
    });
    stage.parentNode.insertBefore(list, stage.nextSibling);

    on(controls, 'change', function () { setTimeout(extRefresh, 0); });
    on(controls, 'input', function () { setTimeout(extRefresh, 0); });
    ['ThermoAspenExteriorDesignControllerUpdated', 'ThermoAspenSaunaDesignerUpdated', 'ThermoAspenRoofFinishProviderReady'].forEach(function (n) { on(W, n, function () { setTimeout(extRefresh, 0); }); });
    if ('MutationObserver' in W) new MutationObserver(function () { if (!ext.open) extRefresh(); }).observe(controls, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
    on(W, 'ThermoAspenDesignerTabChanged', function () { extClose(); });
    on(D, 'keydown', function (e) { if (e.key === 'Escape' && ext.open && !sheetState) { e.preventDefault(); extClose(); } });
    extRefresh();
  }


  /* ---------- Layout tools (rows under the floor plan) ----------
     Room, Door, Windows, Stove and Benches. Stove and Benches open the floor plan's own
     panels (#ti-stove-picker) inside a bottom sheet; the panels' code is unchanged. */
  var lay = { rows: {}, box: null, zonesBtn: null };
  var CHEV = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"></path></svg>';
  var WALL_NAMES = { front: 'Front wall', back: 'Back wall', left: 'Left wall', right: 'Right wall' };
  function designerData() { return W.ThermoAspenSaunaDesignerData || null; }
  function layValues() {
    var d = designerData() || {}, out = {};
    var dim = d.dimensions || {};
    out.room = dim.lengthMm && dim.widthMm ? Math.round(dim.lengthMm) + ' × ' + Math.round(dim.widthMm) + ' mm' : '';
    var oc = Array.isArray(d.openingCollection) ? d.openingCollection : [];
    var door = oc.find(function (o) { return o && o.type === 'door' && o.enabled !== false; }) || (d.layout && d.layout.door) || null;
    var doorText = door && WALL_NAMES[door.wall] ? 'door ' + WALL_NAMES[door.wall].replace(' wall', '').toLowerCase() : '';
    var wins = oc.filter(function (o) { return o && o.type === 'window' && o.enabled !== false; });
    var winText;
    if (!wins.length) winText = 'No windows';
    else {
      var walls = [];
      wins.forEach(function (w) { var n = (WALL_NAMES[w.wall] || w.wall || '').replace(' wall', '').toLowerCase(); if (n && walls.indexOf(n) < 0) walls.push(n); });
      winText = wins.length + (wins.length === 1 ? ' window' : ' windows') + (walls.length === 1 ? ' ' + walls[0] : '');
    }
    out.windows = winText + (doorText ? ' · ' + doorText : '');
    var m = D.querySelector('#ti-stove-compact-summary .ti-stove-summary-model');
    out.stove = m ? m.textContent.replace(/^Harvia\s+/, '') : '';
    try {
      var st = W.__ThermoAspenDesignerV5 && W.__ThermoAspenDesignerV5.getBenchDesignState && W.__ThermoAspenDesignerV5.getBenchDesignState();
      var mode = st ? String(st.mode) : '';
      out.benches = mode === 'none' ? 'None' : mode === '3' ? '3 tiers' : mode === '2' ? '2 tiers' : '';
    } catch (e) { out.benches = ''; }
    return out;
  }
  function layRefresh() {
    var v = layValues();
    Object.keys(lay.rows).forEach(function (k) { if (lay.rows[k].val) lay.rows[k].val.textContent = v[k] || ''; });
    if (lay.zonesBtn) { var z = $('ti-heater-show-zones'); lay.zonesBtn.setAttribute('aria-pressed', !z || z.checked ? 'true' : 'false'); }
  }
  function layRow(key, label, opts) {
    var row = el(opts.button ? 'button' : 'div', 'ta-tool-row' + (opts.button ? '' : ' ta-tool-row--static'));
    if (opts.button) { row.type = 'button'; row.setAttribute('data-ta-layout-open', key); }
    row.appendChild(el('span', 'ta-tool-row-name', label));
    var val = el('span', 'ta-tool-row-value', '');
    row.appendChild(val);
    if (opts.info) row.appendChild(infoButton(opts.info, 'About the ' + label.toLowerCase()));
    else { var c = el('span', 'ta-tool-row-chev'); c.setAttribute('aria-hidden', 'true'); c.innerHTML = CHEV; row.appendChild(c); }
    lay.rows[key] = { row: row, val: val };
    return row;
  }
  function openObjectPanel(kind) {
    var btn = D.querySelector('#ti3d-controls [data-ti-panel-btn="' + kind + '"]');
    if (!btn) { showTab('interior'); return; }
    var box = $('ti-stove-picker');
    if (!box || box.hidden) btn.click();
    box = $('ti-stove-picker');
    if (!box || box.hidden) return;
    lay.box = box;
    var row = lay.rows[kind === 'bench' ? 'benches' : 'stove'];
    openSheet({ title: kind === 'bench' ? 'Benches' : 'Stove', node: box, kind: 'object', onClose: function () {
      if (!box.hidden) { var x = box.querySelector('[data-sp-close]'); if (x) x.click(); else box.hidden = true; }
      layRefresh();
    } });
    if (row) sheetState.returnFocus = row.row;
    var first = box.querySelector('[aria-pressed="true"]') || box.querySelector('button:not([data-sp-close])');
    if (first) try { first.focus({ preventScroll: true }); } catch (e) {}
  }
  function openWindows() {
    // Stage 6: the flat-wall Windows editor. Falls back to the 3D window editor if it isn't loaded.
    var E = W.ThermoAspenWindowsEditorV1;
    if (E && typeof E.open === 'function' && E.open() !== false) return;
    showTab('interior');
    var t0 = Date.now();
    (function tryOpen() {
      var b = D.querySelector('#ti-sauna-3d-preview .ti3d-edit-toggle');
      if (b && !b.hidden && b.offsetParent !== null) { if (b.getAttribute('aria-pressed') !== 'true') b.click(); return; }
      if (Date.now() - t0 < 10000) setTimeout(tryOpen, 250);
    })();
  }
  function setupLayoutTools() {
    var p = panels.layout; if (!p) return;
    var card = p.root.querySelector('#ti-custom-body > .ti-card') || p.root.querySelector('.ti-layout-plan-card');
    if (!card || !card.parentNode) return;
    var list = el('div', 'ta-tool-rows ta-layout-rows');
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', 'Layout settings');
    list.appendChild(layRow('room', 'Room', { button: true }));
    list.appendChild(layRow('windows', 'Windows/Door', { button: true }));
    list.appendChild(layRow('stove', 'Stove', { button: true }));
    list.appendChild(layRow('benches', 'Benches', { button: true }));
    card.parentNode.insertBefore(list, card.nextSibling);

    on(list, 'click', function (e) {
      var b = e.target.closest && e.target.closest('[data-ta-layout-open]');
      if (!b) return;
      var k = b.getAttribute('data-ta-layout-open');
      if (k === 'stove') openObjectPanel('stove');
      else if (k === 'benches') openObjectPanel('bench');
      else if (k === 'windows') openWindows();
      else if (k === 'room') {
        var tag = D.querySelector('#ti-layout-plan [data-room-dim]');
        if (tag) {
          var wrap = D.querySelector('#ti-sauna-layout .ti-layout-plan-wrap');
          if (wrap) { var top = wrap.getBoundingClientRect().top + W.pageYOffset - stickyOffset() - (shell.offsetHeight || 0) - 8; W.scrollTo(0, Math.max(0, top)); }
          // The plan opens its room-size form on pointer-down or Enter on this tag.
          tag.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
        }
      }
    });

    // Clearance outline on/off, beside the Floor plan heading.
    var head = p.wrap.querySelector('.ta-panel-head');
    if (head && $('ti-heater-show-zones')) {
      var z = el('button', 'ta-info-btn ta-zones-btn');
      z.type = 'button';
      z.setAttribute('aria-label', 'Show stove clearance');
      z.setAttribute('aria-pressed', 'true');
      z.innerHTML = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="3 2"><rect x="4" y="4" width="16" height="16" rx="2"></rect></svg>';
      var grp = el('span', 'ta-panel-head-actions');
      var info = head.querySelector('.ta-info-btn');
      head.insertBefore(grp, info);
      grp.appendChild(z);
      if (info) grp.appendChild(info);
      on(z, 'click', function () {
        var x = $('ti-heater-show-zones'); if (!x) return;
        x.checked = !x.checked;
        x.dispatchEvent(new Event('change', { bubbles: true }));
        layRefresh();
      });
      lay.zonesBtn = z;
    }

    // Close the sheet when the floor plan closes its own panel (stove chosen, ×, tap outside).
    if ('MutationObserver' in W) new MutationObserver(function () {
      var box = $('ti-stove-picker');
      if (box && box.hidden && sheetState && sheetState.node === box) closeSheet();
    }).observe(D.body, { attributes: true, attributeFilter: ['hidden'], subtree: true });

    ['ThermoAspenSaunaDesignerUpdated', 'ThermoAspenSaunaSceneUpdated', 'ThermoAspenOpeningCollectionUpdated', 'ThermoAspenHeaterCatalogueReady', 'ThermoAspenWindowsEditorClosed'].forEach(function (n) { on(W, n, function () { setTimeout(layRefresh, 0); }); });
    layRefresh();
    setTimeout(layRefresh, 1500);
  }



  /* ---------- expand button (Interior, Exterior and Summary viewers) ----------
     One button in the top-right corner of a viewer fills the screen with it; the same
     button (or Escape, or changing step) puts it back. The viewers resize themselves. */
  var EXPAND_SVG = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"></path></svg>';
  var COLLAPSE_SVG = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"></path></svg>';
  var expanded = null;
  function setExpanded(stage, on) {
    if (!stage) return;
    var b = stage.querySelector(':scope > .ta-expand-btn');
    if (on && expanded && expanded !== stage) setExpanded(expanded, false);
    stage.classList.toggle('ta-expanded', !!on);
    D.documentElement.classList.toggle('ta-viewer-expanded', !!on);
    expanded = on ? stage : null;
    if (b) {
      b.innerHTML = on ? COLLAPSE_SVG : EXPAND_SVG;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen');
    }
    notifyResize();
  }
  function addExpand(stage) {
    if (!stage || stage.querySelector(':scope > .ta-expand-btn')) return;
    var b = el('button', 'ta-expand-btn'); b.type = 'button';
    b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', 'Full screen'); b.innerHTML = EXPAND_SVG;
    on(b, 'click', function (e) { e.preventDefault(); e.stopPropagation(); setExpanded(stage, !stage.classList.contains('ta-expanded')); });
    on(b, 'pointerdown', function (e) { e.stopPropagation(); });
    stage.appendChild(b);
  }

  /* ---------- Interior tools (rows under the 3D view) ----------
     Wall finish: board size and direction for the walls and ceiling (Designer setFinish).
     Window view: the scene behind the windows; the viewer's own picture list moves into a sheet. */
  var inter = { rows: {}, finish: null };
  function profileLabel(p) { var m = /(\d+)\s*x\s*(\d+)/i.exec(String(p || '')); return m ? m[2] + ' mm boards' : String(p || ''); }
  function interValues() {
    var A = W.__ThermoAspenDesignerV5, f = null, out = {};
    try { f = A && A.getFinish ? A.getFinish() : null; } catch (e) { f = null; }
    out.finish = f ? profileLabel(f.wallProfile) + ' · ' + (f.wallDirection === 'horizontal' ? 'horizontal' : 'vertical') : '';
    var wv = $('ti3d-window-views'), act = wv && wv.querySelector('.ti3d-wv.is-active .ti3d-wv-label');
    out.view = !wv || wv.hidden ? 'No windows' : act ? act.textContent.trim() : '';
    var lv = layValues(), tg = iwToggle();
    out.windows = tg && tg.classList.contains('ti-we-needs-adjustment') ? 'Needs adjusting' : lv.windows;
    return out;
  }
  function interRefresh() {
    var v = interValues();
    Object.keys(inter.rows).forEach(function (k) { inter.rows[k].val.textContent = v[k] || ''; });
    if (inter.finish && sheetState && sheetState.node === inter.finish) finishSync();
  }
  function seg(name, label, options) {
    var g = el('fieldset', 'ta-seg-field');
    g.appendChild(el('legend', 'ta-seg-legend', label));
    var row = el('div', 'ta-seg'); row.setAttribute('role', 'group');
    options.forEach(function (o) {
      var b = el('button', 'ta-seg-btn', o.label); b.type = 'button';
      b.setAttribute('data-name', name); b.setAttribute('data-value', o.value); b.setAttribute('aria-pressed', 'false');
      row.appendChild(b);
    });
    g.appendChild(row);
    return g;
  }
  function finishPanel() {
    if (inter.finish) return inter.finish;
    var A = W.__ThermoAspenDesignerV5, opts = A && A.getFinishOptions ? A.getFinishOptions() : { profiles: [] };
    var box = el('div', 'ta-finish');
    box.appendChild(seg('wallProfile', 'Board size', (opts.profiles || []).map(function (p) { return { value: p, label: profileLabel(p).replace(' boards', '') }; })));
    box.appendChild(seg('wallDirection', 'Wall boards', [{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]));
    box.appendChild(seg('ceilingDirection', 'Ceiling boards', [{ value: 'parallel', label: 'Along the room' }, { value: 'perpendicular', label: 'Across the room' }]));
    box.appendChild(el('p', 'ta-finish-note', 'STS4 profile in thermo-aspen. The board size is used on the walls and the ceiling.'));
    on(box, 'click', function (e) {
      var b = e.target.closest && e.target.closest('.ta-seg-btn'); if (!b) return;
      var patch = {}; patch[b.getAttribute('data-name')] = b.getAttribute('data-value');
      var Ad = W.__ThermoAspenDesignerV5;
      if (Ad && Ad.setFinish) { try { Ad.setFinish(patch); } catch (err) {} }
      finishSync(); interRefresh();
    });
    inter.finish = box;
    return box;
  }
  function finishSync() {
    var A = W.__ThermoAspenDesignerV5, f = null;
    try { f = A && A.getFinish ? A.getFinish() : null; } catch (e) { f = null; }
    if (!f || !inter.finish) return;
    Array.prototype.forEach.call(inter.finish.querySelectorAll('.ta-seg-btn'), function (b) {
      b.setAttribute('aria-pressed', String(f[b.getAttribute('data-name')]) === b.getAttribute('data-value') ? 'true' : 'false');
    });
  }
  function openWindowView() {
    var wv = $('ti3d-window-views');
    if (!wv || wv.hidden) {
      var note = el('div', 'ta-info-body');
      note.appendChild(el('p', null, 'There are no windows yet. Add one in Layout › Windows/Door to choose the view through it.'));
      openSheet({ title: 'Window view', node: note, kind: 'object', infoKey: 'interior.windowView' });
      return;
    }
    openSheet({ title: 'Window view', node: wv, kind: 'object', infoKey: 'interior.windowView', onClose: interRefresh });
    if (inter.rows.view) sheetState.returnFocus = inter.rows.view.row;
  }
  /* 1.5.1: Interior › Windows opens the 3D window and wall editor in the viewer (the old
     "Adjust Windows" button, which stays hidden). While it is open the Interior rows are
     replaced by one Done button under the viewer. */
  function iwToggle() { return D.querySelector('#ti-sauna-3d-preview .ti3d-edit-toggle'); }
  function iwOn() { var t = iwToggle(); return !!t && t.getAttribute('aria-pressed') === 'true'; }
  function iwSet(on) {
    var t = iwToggle(); if (!t) return;
    if (on !== iwOn()) t.click();
    iwSync();
    if (on) {
      var st = $('ti3d-stage');
      if (st) { var top = st.getBoundingClientRect().top + W.pageYOffset - stickyOffset() - (shell.offsetHeight || 0) - 8; W.scrollTo(0, Math.max(0, top)); }
      if (inter.done) try { inter.done.focus({ preventScroll: true }); } catch (e) {}
    } else if (inter.rows.windows) try { inter.rows.windows.row.focus({ preventScroll: true }); } catch (e) {}
  }
  function iwSync() {
    var on = iwOn();
    D.documentElement.classList.toggle('ta-iw-editing', on);
    if (inter.done) inter.done.hidden = !on;
    if (inter.list) inter.list.hidden = on;
    interRefresh();
  }
  function setupInteriorTools() {
    var p = panels.interior; if (!p) return;
    var stage = $('ti3d-stage'), card = stage && stage.closest('.ti3d-card');
    if (!card || !card.parentNode) return;
    var list = el('div', 'ta-tool-rows ta-interior-rows');
    list.setAttribute('role', 'group'); list.setAttribute('aria-label', 'Interior settings');
    [['windows', 'Windows/Door'], ['finish', 'Wall finish'], ['view', 'Window view']].forEach(function (r) {
      var b = el('button', 'ta-tool-row'); b.type = 'button'; b.setAttribute('data-ta-interior-open', r[0]);
      b.appendChild(el('span', 'ta-tool-row-name', r[1]));
      var val = el('span', 'ta-tool-row-value', '');
      b.appendChild(val);
      var c = el('span', 'ta-tool-row-chev'); c.setAttribute('aria-hidden', 'true'); c.innerHTML = CHEV; b.appendChild(c);
      list.appendChild(b);
      inter.rows[r[0]] = { row: b, val: val };
    });
    card.parentNode.insertBefore(list, card.nextSibling);
    inter.list = list;
    var done = el('button', 'ta-tool-done ta-iw-done', 'Done'); done.type = 'button'; done.hidden = true;
    done.setAttribute('aria-label', 'Finish editing windows and door');
    list.parentNode.insertBefore(done, list.nextSibling);
    inter.done = done;
    on(done, 'click', function () { iwSet(false); });
    var tg = iwToggle();
    if (tg && 'MutationObserver' in W) new MutationObserver(function () { setTimeout(iwSync, 0); }).observe(tg, { attributes: true, attributeFilter: ['aria-pressed', 'class', 'data-issue-count'] });
    on(W, 'ThermoAspenDesignerTabChanged', function () { if (iwOn()) iwSet(false); });
    on(list, 'click', function (e) {
      var b = e.target.closest && e.target.closest('[data-ta-interior-open]'); if (!b) return;
      var k = b.getAttribute('data-ta-interior-open');
      if (k === 'windows') { iwSet(true); return; }
      if (k === 'finish') {
        openSheet({ title: 'Wall finish', node: finishPanel(), kind: 'object', infoKey: 'interior.finish', onClose: interRefresh });
        sheetState.returnFocus = b; finishSync();
      } else openWindowView();
    });
    // The picture list closes its sheet once a scene is picked.
    var wv = $('ti3d-window-views');
    if (wv) on(wv, 'click', function (e) { if (e.target.closest && e.target.closest('.ti3d-wv')) setTimeout(function () { interRefresh(); if (sheetState && sheetState.node === wv) closeSheet(); }, 350); });
    if ('MutationObserver' in W && wv) new MutationObserver(function () { setTimeout(interRefresh, 0); }).observe(wv, { attributes: true, subtree: true, attributeFilter: ['hidden', 'class'] });
    ['ThermoAspenSaunaDesignerUpdated', 'ThermoAspenOpeningCollectionUpdated'].forEach(function (n) { on(W, n, function () { setTimeout(interRefresh, 0); }); });
    interRefresh();
    setTimeout(interRefresh, 1500);
  }

  /* ---------- Summary step ----------
     The Summary (thermoaspen-designer-summary-v1.js, 1.3.0+) is drawn inside this step.
     Its 3D construction view loads when the step opens and is released when it closes. */
  var sum = { host: null, active: false };
  function summaryOn() {
    sum.active = true;
    var Sm = W.ThermoAspenDesignerSummaryV1;
    if (!Sm || typeof Sm.mount !== 'function') {
      sum.host.innerHTML = '';
      sum.host.appendChild(el('p', 'ta-summary-wait', 'Loading the summary…'));
      return;
    }
    if (!sum.mounted) { sum.host.innerHTML = ''; Sm.mount(sum.host); sum.mounted = true; }
    Sm.open();
    addExpand(sum.host.querySelector('.ta-sum-frame'));
  }
  function summaryOff() {
    if (!sum.active) return;
    sum.active = false;
    var Sm = W.ThermoAspenDesignerSummaryV1;
    if (Sm && sum.mounted && Sm.isOpen && Sm.isOpen()) Sm.close();
  }
  function setupSummaryStep() {
    var p = panels.summary; if (!p) return;
    sum.host = el('div', 'ta-summary-host');
    p.root.appendChild(sum.host);
    on(W, 'ThermoAspenDesignerTabChanged', function (e) { var t = e && e.detail && e.detail.tab; if (t === 'summary') summaryOn(); else summaryOff(); });
    on(W, 'ThermoAspenDesignerSummaryReady', function () { if (sum.active && !sum.mounted) summaryOn(); });
  }
  function ensureSummaryRoot() {
    if ($('ta-summary-step')) return;
    var ext = $('ti-sauna-exterior-viewer'); if (!ext) return;
    var w = sectionWrap(ext);
    var s = el('div', 'shopify-section ta-summary-step'); s.id = 'ta-summary-step';
    w.parentNode.insertBefore(s, w.nextSibling);
  }

  /* On Shopify preview links, the "Draft" preview bar is fixed to the bottom of the screen.
     Lift the bottom bar and sheets above it. (The live site has no preview bar.) */
  /* Total height of the fixed/sticky chrome (top bar + bottom bar + preview bar), so the
     viewers can size themselves to the space left between them. */
  function measureChrome() {
    var top = shell ? shell.offsetHeight : 0, bot = bottomEl ? bottomEl.offsetHeight : 0;
    var pb = parseFloat(getComputedStyle(D.documentElement).getPropertyValue('--ta-preview-bar')) || 0;
    D.documentElement.style.setProperty('--ta-chrome', Math.round(top + bot + pb) + 'px');
    D.documentElement.style.setProperty('--ta-shell-h', Math.round(top) + 'px');
    tunePlanLabels();
  }
  function measurePreviewBar() {
    var b = D.getElementById('PBarNextFrameWrapper') || D.getElementById('preview-bar-iframe');
    var h = 0;
    if (b && b.offsetHeight > 0) { var r = b.getBoundingClientRect(); if (r.bottom >= W.innerHeight - 2) h = Math.round(r.height); }
    D.documentElement.style.setProperty('--ta-preview-bar', h + 'px');
    measureChrome();
  }

  /* ---------- build ---------- */
  function build() {
    shell = $(ROOT_ID);
    if (!shell) return false;
    // Lift the bar out of its own section wrapper: a sticky element only sticks
    // within its parent, and the wrapper is exactly as tall as the bar.
    var ownWrap = sectionWrap(shell);
    if (ownWrap !== shell && ownWrap.parentNode) ownWrap.parentNode.insertBefore(shell, ownWrap);

    // The Summary step has no section of its own: it is made here, after Exterior.
    ensureSummaryRoot();
    // Find the component sections. If any is missing, leave the page as it is.
    var ok = true;
    TABS.forEach(function (t) {
      var root = $(t.root);
      if (!root) { ok = false; return; }
      panels[t.name] = { root: root, wrap: sectionWrap(root), def: t };
    });
    if (!ok) { try { console.warn('ThermoAspen Designer shell: a Designer section is missing; shell not applied.'); } catch (e) {} panels = {}; return false; }

    bar = shell.querySelector('.ta-shell-bar');
    var tablist = shell.querySelector('.ta-tabs');
    saveTick = shell.querySelector('.ta-save');
    saveLabel = shell.querySelector('.ta-save-label');
    nextBtn = shell.querySelector('.ta-next');
    sheet = shell.querySelector('.ta-sheet');
    sheetBody = shell.querySelector('.ta-sheet-body');
    sheetTitle = shell.querySelector('.ta-sheet-title');
    sheetInfoBtn = shell.querySelector('.ta-sheet-info');
    backdrop = shell.querySelector('.ta-sheet-backdrop');

    // Fixed layers live at the end of <body> so no section's stacking context can trap them.
    var bottom = bottomEl = shell.querySelector('.ta-bottom');
    saveLabel = bottom.querySelector('.ta-save-label');
    nextBtn = bottom.querySelector('.ta-next');
    D.body.appendChild(bottom);
    D.body.appendChild(backdrop);
    D.body.appendChild(sheet);

    tablist.style.setProperty('--ta-tab-count', String(TABS.length));
    TABS.forEach(function (t, i) {
      var p = panels[t.name];
      var tab = el('button', 'ta-tab', t.label);
      tab.type = 'button';
      tab.id = 'ta-tab-' + t.name;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', p.root.id);
      tab.setAttribute('data-tab', t.name);
      tablist.appendChild(tab);
      p.tab = tab;

      p.wrap.classList.add('ta-shell-panel');
      p.wrap.setAttribute('data-ta-panel', t.name);
      p.root.setAttribute('role', 'tabpanel');
      p.root.setAttribute('aria-labelledby', 'ta-tab-' + t.name);

      var head = el('div', 'ta-panel-head');
      var h = el('h2', 'ta-panel-title', t.heading);
      head.appendChild(h);
      head.appendChild(infoButton(t.info, 'About the ' + t.heading.toLowerCase()));
      p.wrap.insertBefore(head, p.wrap.firstChild);
    });

    setupExteriorTools();
    setupLayoutTools();
    setupInteriorTools();
    setupSummaryStep();
    addExpand($('ti3d-stage'));
    addExpand($('tiext-stage'));
    on(W, 'ThermoAspenDesignerTabChanged', function () { if (expanded) setExpanded(expanded, false); });
    on(D, 'keydown', function (e) { if (e.key === 'Escape' && expanded && !sheetState) { e.preventDefault(); setExpanded(expanded, false); } });

    // The project actions and the workflow nav move into sheets while open.
    var projects = $('ti-design-projects');
    var workflow = $('ti-workflow-nav');
    if (projects) sectionWrap(projects).classList.add('ta-shell-hidden-source');
    if (workflow) sectionWrap(workflow).classList.add('ta-shell-hidden-source');

    var stepBtn = shell.querySelector('.ta-step');
    // Phones show a short '1/4' label on the step chip; wide screens keep 'Step 1 of 4'.
    if (stepBtn) {
      var stepText = (stepBtn.textContent || '').trim(), mm = /(\d+)\s*of\s*(\d+)/i.exec(stepText);
      if (mm) {
        var svgEl = stepBtn.querySelector('svg');
        stepBtn.textContent = '';
        stepBtn.appendChild(el('span', 'ta-step-long', stepText));
        stepBtn.appendChild(el('span', 'ta-step-short', mm[1] + '/' + mm[2]));
        if (svgEl) stepBtn.appendChild(svgEl);
      }
    }
    var menuBtn = shell.querySelector('.ta-menu');
    // 1.5.0: the step bar shows where you are, so the step chip is no longer shown.
    if (stepBtn) stepBtn.hidden = true;
    if (!projects) menuBtn.hidden = true;

    on(stepBtn, 'click', function () { if (workflow) openSheet({ title: 'Steps', node: workflow, kind: 'steps' }); });
    on(menuBtn, 'click', function () { if (projects) openSheet({ title: 'Your design', node: projects, kind: 'projects', infoKey: 'summary.saved' }); });

    on(tablist, 'click', function (e) {
      var b = e.target.closest && e.target.closest('.ta-tab');
      if (b) showTab(b.getAttribute('data-tab'));
    });
    on(tablist, 'keydown', function (e) {
      var keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
      if (!(e.key in keys)) return;
      e.preventDefault();
      var i = TABS.findIndex(function (t) { return t.name === active; });
      var k = keys[e.key];
      i = k === 'first' ? 0 : k === 'last' ? TABS.length - 1 : (i + k + TABS.length) % TABS.length;
      showTab(TABS[i].name);
      panels[TABS[i].name].tab.focus();
    });

    on(nextBtn, 'click', function () {
      var n = nextBtn.getAttribute('data-next');
      if (n && n !== 'construction') { showTab(n, { focusHeading: true }); return; }
      var h = W.ThermoAspenProjectHandoff;
      if (h && typeof h.goToConstruction === 'function') { h.goToConstruction(); return; }
      W.location.href = '/pages/construction-test-page';
    });

    on(bottom.querySelector('.ta-save-info'), 'click', function () { openInfo('summary.saved'); });
    on(sheet.querySelector('.ta-sheet-close'), 'click', function () { closeSheet(); });
    on(backdrop, 'click', function () { closeSheet(); });
    on(sheetInfoBtn, 'click', function () { var k = sheetInfoBtn.getAttribute('data-ta-info'); if (k) openInfo(k); });
    on(D, 'keydown', function (e) { if (e.key === 'Escape' && sheetState) { e.preventDefault(); closeSheet(); } else trapFocus(e); });

    // Any [data-ta-info] button anywhere on the page opens its text.
    on(D, 'click', function (e) {
      var b = e.target.closest && e.target.closest('[data-ta-info]');
      if (!b || b === sheetInfoBtn) return;
      e.preventDefault();
      openInfo(b.getAttribute('data-ta-info'));
    });

    // Steps: close the sheet when a workflow step navigates away or is chosen.
    if (workflow) on(workflow, 'click', function (e) { if (e.target.closest && e.target.closest('.ti-workflow-step')) setTimeout(function () { if (sheetState && sheetState.kind === 'steps') closeSheet(); }, 0); });

    ['pointerdown', 'keydown', 'click'].forEach(function (t) { on(D, t, function () { lastGesture = Date.now(); }, true); });
    patchScrollIntoView();

    on(W, 'ThermoAspenSaunaDesignerUpdated', markSaving);
    on(W, 'resize', measureHeader);
    on(W, 'hashchange', function () { var t = (W.location.hash || '').slice(1); if (tabDef(t)) showTab(t, { updateHash: false }); });

    measureHeader();
    measurePreviewBar();
    [1500, 4000, 8000].forEach(function (ms) { setTimeout(measurePreviewBar, ms); });
    on(W, 'resize', measurePreviewBar);
    on(W, 'resize', measureChrome);
    setTimeout(measureChrome, 300);
    D.documentElement.classList.add('ta-shell-on');
    shell.hidden = false;

    var start = (W.location.hash || '').slice(1);
    if (!tabDef(start)) start = store();
    if (!tabDef(start)) start = 'layout';
    showTab(start, { updateHash: false, keepScroll: true });
    setTimeout(measureHeader, 600);
    return true;
  }
  function on(t, ev, fn, cap) { if (t) t.addEventListener(ev, fn, cap || false); }

  var api = {
    version: VERSION,
    showTab: function (n) { return showTab(n); },
    currentTab: function () { return active; },
    openSheet: openSheet,
    closeSheet: function () { closeSheet(); },
    openInfo: openInfo
  };
  W.ThermoAspenDesignerShell = api;

  function init() {
    try { api.ready = build(); }
    catch (err) {
      api.ready = false;
      try { console.error('ThermoAspen Designer shell failed; the page keeps its previous layout.', err); } catch (e) {}
      D.documentElement.classList.remove('ta-shell-on');
      Element.prototype.scrollIntoView = nativeScrollIntoView;
    }
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', init); else init();
})(window, document);
